import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import Select from "react-select";
import { Link, useParams, useNavigate } from "react-router-dom";
import { fetchUserById, updateUser } from "../../services/userService";
import { fetchAllDepartments } from "../../services/departmentService";
import LoadingSpinner from "../../components/Common/LoadingSpinner";
import Breadcrumb from "../../components/Common/Breadcrumb";
import { message } from "antd";
import { decrypt } from "../../utils/encryption";
import { jwtDecode } from "jwt-decode";

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const decryptedId = decrypt(id);
  const [loggedInUserData, setLoggedInUserData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [selectedImage, setSelectedImage] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const [userData, setUserData] = useState({
    user_name: "",
    user_contact: "",
    user_email: "",
    user_biography: "",
    user_designation: "",
    user_qualification: "",
    user_department: { id: null, name: null },
    user_teaching_experience: "",
    user_industrial_experience: "",
    user_specialization: "",
    user_international_publication: "",
    user_domestic_publication: "",
    user_research_project: "",
    user_facebook_link: "",
    user_linkedin_link: "",
    user_status: "1",
    user_profile_img: "",
  });

  // Fetch user and departments
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const departments = await fetchAllDepartments();
        const formattedDepts = [
          { value: null, label: "None" },
          ...departments.map((dept) => ({
            value: dept.department_id,
            label: dept.department_name,
          })),
        ];
        setDepartmentOptions(formattedDepts);

        const user = await fetchUserById(decryptedId);
        setUserData((prev) => ({
          ...prev,
          ...user,
          user_department: {
            id: user.department_id || null,
            name: user.department_name || null,
          },
        }));

        const deptOption = formattedDepts.find(
          (dept) => dept.value === (user.department_id || null)
        );
        setSelectedDepartment(deptOption || formattedDepts[0]);
      } catch (error) {
        console.error("Failed to fetch data:", error);
        message.error("Failed to fetch user data");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [decryptedId]);

  useEffect(() => {
      const fetchAndSetUser = async () => {
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const decoded = jwtDecode(token);
            const user = await fetchUserById(decoded.id);
            setLoggedInUserData(user);
          } catch (error) {
            console.error("Error decoding token or fetching user:", error);
            localStorage.removeItem("token");
            setLoggedInUserData(null);
          } finally {
            setLoading(false);
          }
        } else {
          setLoading(false);
        }
      };
  
      fetchAndSetUser();
    }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDepartmentChange = (selectedOption) => {
    setSelectedDepartment(selectedOption);
    setUserData((prev) => ({
      ...prev,
      user_department: selectedOption
        ? { id: selectedOption.value, name: selectedOption.label }
        : { id: null, name: null },
    }));
  };

  const handleStatusChange = (e) => {
    setUserData((prev) => ({
      ...prev,
      user_status: e.target.value,
    }));
  };

  const loadFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      setSelectedImage(file);
      setUserData((prev) => ({
        ...prev,
        user_profile_img: URL.createObjectURL(file),
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("name", userData.user_name);
      formData.append("contact", userData.user_contact);
      formData.append("email", userData.user_email);
      formData.append("user_biography", userData.user_biography);
      formData.append("designation", userData.user_designation);
      formData.append("qualification", userData.user_qualification);
      formData.append("department_id", userData.user_department?.id || "");
      formData.append("department_name", userData.user_department?.name || "");
      formData.append("teaching_experience", userData.user_teaching_experience);
      formData.append("industrial_experience", userData.user_industrial_experience);
      formData.append("specialization", userData.user_specialization);
      formData.append("international_publication", userData.user_international_publication);
      formData.append("domestic_publication", userData.user_domestic_publication);
      formData.append("research_project", userData.user_research_project);
      formData.append("facebook_link", userData.user_facebook_link);
      formData.append("linkedin_link", userData.user_linkedin_link);
      formData.append("status", userData.user_status);

      const parameter = loggedInUserData?.user_role === "1" ? "Admin" : "Department";

      formData.append("paramter", parameter);
      formData.append("updatedbyemail", loggedInUserData.user_email);

      if (selectedImage) {
        formData.append("profile_img", selectedImage);
      }

      await updateUser(decryptedId, formData);
      message.success("User updated successfully!");
      navigate("/list-users");
    } catch (error) {
      console.error(error);
      message.error("An error occurred while updating the user");
    } finally {
      setLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: "User", path: "/list-users" },
    { label: "Edit User" },
  ];

  return (
    <>
      <Helmet>
        <title>Edit Member | CRM Jnec</title>
      </Helmet>
      <div className="main-wrapper">
        <Header />
        <Sidebar />
        <div className="page-wrapper">
          <div className="content">
            <div className="page-header">
              <div className="row">
                <div className="col-sm-12">
                  <Breadcrumb items={breadcrumbItems} />
                </div>
              </div>
            </div>

            {loading ? (
              <LoadingSpinner message="Loading user data..." />
            ) : (
              <div className="row">
                <div className="col-sm-12">
                  <div className="card">
                    <div className="card-body">
                      <form onSubmit={handleSubmit}>
                        <div className="row">
                          {/* Name */}
                          <div className="col-12 col-md-6 col-xl-12">
                            <div className="form-group local-forms">
                              <label>
                                Name <span className="login-danger">*</span>
                              </label>
                              <input
                                className="form-control"
                                type="text"
                                name="user_name"
                                value={userData.user_name || ""}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                          </div>

                          {/* Contact */}
                          <div className="col-12 col-md-6 col-xl-6">
                            <div className="form-group local-forms">
                              <label>
                                Mobile <span className="login-danger">*</span>
                              </label>
                              <input
                                className="form-control"
                                type="text"
                                name="user_contact"
                                value={userData.user_contact || ""}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                          </div>

                          {/* Email */}
                          <div className="col-12 col-md-6 col-xl-6">
                            <div className="form-group local-forms">
                              <label>
                                Email <span className="login-danger">*</span>
                              </label>
                              <input
                                className="form-control"
                                type="email"
                                name="user_email"
                                value={userData.user_email || ""}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                          </div>

                          {/* Biography */}
                          <div className="col-12 col-sm-12">
                            <div className="form-group local-forms">
                              <label>Biography</label>
                              <textarea
                                className="form-control"
                                rows={3}
                                name="user_biography"
                                value={userData.user_biography || ""}
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>

                          {/* Designation */}
                          <div className="col-12 col-md-6 col-xl-4">
                            <div className="form-group local-forms">
                              <label>
                                Designation{" "}
                                <span className="login-danger">*</span>
                              </label>
                              <input
                                className="form-control"
                                type="text"
                                name="user_designation"
                                value={userData.user_designation || ""}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                          </div>

                          {/* Qualification */}
                          <div className="col-12 col-md-6 col-xl-4">
                            <div className="form-group local-forms">
                              <label>
                                Qualification{" "}
                                <span className="login-danger">*</span>
                              </label>
                              <input
                                className="form-control"
                                type="text"
                                name="user_qualification"
                                value={userData.user_qualification || ""}
                                onChange={handleInputChange}
                                required
                              />
                            </div>
                          </div>

                          {/* Department */}
                          <div className="col-12 col-md-6 col-xl-4">
                            <div className="form-group local-forms">
                              <label>
                                Department{" "}
                                <span className="login-danger">*</span>
                              </label>
                              <Select
                                value={selectedDepartment}
                                onChange={handleDepartmentChange}
                                options={departmentOptions}
                                components={{ IndicatorSeparator: () => null }}
                                styles={{
                                  control: (base, state) => ({
                                    ...base,
                                    borderColor: state.isFocused
                                      ? "#2e37a4"
                                      : "rgba(46, 55, 164, 0.1)",
                                    boxShadow: "none",
                                    borderRadius: "10px",
                                    fontSize: "14px",
                                    minHeight: "45px",
                                    backgroundColor:
                                      loggedInUserData?.user_role === "2"
                                        ? "#f5f5f5"
                                        : "white",
                                  }),
                                  menu: (base) => ({ ...base, zIndex: 9999 }),
                                }}
                                menuPortalTarget={document.body}
                                isDisabled={
                                  loading || loggedInUserData?.user_role === "2"
                                }
                              />
                            </div>
                          </div>

                          {/* Teaching Experience */}
                          <div className="col-12 col-md-6 col-xl-4">
                            <div className="form-group local-forms">
                              <label>Teaching Experience</label>
                              <input
                                className="form-control"
                                type="text"
                                name="user_teaching_experience"
                                value={userData.user_teaching_experience || ""}
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>

                          {/* Industrial Experience */}
                          <div className="col-12 col-md-6 col-xl-4">
                            <div className="form-group local-forms">
                              <label>Industrial Experience</label>
                              <input
                                className="form-control"
                                type="text"
                                name="user_industrial_experience"
                                value={
                                  userData.user_industrial_experience || ""
                                }
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>

                          {/* Specialization */}
                          <div className="col-12 col-md-6 col-xl-4">
                            <div className="form-group local-forms">
                              <label>Specialization</label>
                              <input
                                className="form-control"
                                type="text"
                                name="user_specialization"
                                value={userData.user_specialization || ""}
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>

                          {/* International Publications */}
                          <div className="col-12 col-md-6 col-xl-4">
                            <div className="form-group local-forms">
                              <label>International Publications</label>
                              <input
                                className="form-control"
                                type="text"
                                name="user_international_publication"
                                value={
                                  userData.user_international_publication || ""
                                }
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>

                          {/* Domestic Publications */}
                          <div className="col-12 col-md-6 col-xl-4">
                            <div className="form-group local-forms">
                              <label>Domestic Publications</label>
                              <input
                                className="form-control"
                                type="text"
                                name="user_domestic_publication"
                                value={userData.user_domestic_publication || ""}
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>

                          {/* Research Projects */}
                          <div className="col-12 col-md-6 col-xl-4">
                            <div className="form-group local-forms">
                              <label>Research Projects</label>
                              <input
                                className="form-control"
                                type="text"
                                name="user_research_project"
                                value={userData.user_research_project || ""}
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>

                          {/* Social Links */}
                          <div className="col-12 col-md-6 col-xl-6">
                            <div className="form-group local-forms">
                              <label>Facebook profile link</label>
                              <input
                                className="form-control"
                                type="text"
                                name="user_facebook_link"
                                value={userData.user_facebook_link || ""}
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>
                          <div className="col-12 col-md-6 col-xl-6">
                            <div className="form-group local-forms">
                              <label>Linkedin profile link</label>
                              <input
                                className="form-control"
                                type="text"
                                name="user_linkedin_link"
                                value={userData.user_linkedin_link || ""}
                                onChange={handleInputChange}
                              />
                            </div>
                          </div>

                          {/* Profile Image */}
                          <div className="col-12 col-md-6 col-xl-6">
                            <div className="form-group local-top-form">
                              <label className="local-top">Profile Image</label>
                              <div className="settings-btn upload-files-avator d-flex align-items-center gap-2 overflow-hidden">
                                {fileName && (
                                  <span className="file-name text-nowrap">
                                    {fileName}
                                  </span>
                                )}
                                <input
                                  type="file"
                                  accept="image/*"
                                  name="profile_img"
                                  id="profile_img"
                                  onChange={loadFile}
                                  className="hide-input"
                                />
                                <label htmlFor="profile_img" className="upload">
                                  Choose File
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Status */}
                          <div className="col-12 col-md-6 col-xl-6">
                            <div className="form-group select-gender">
                              <label className="gen-label">
                                Status <span className="login-danger">*</span>
                              </label>
                              <div className="form-check-inline">
                                <label className="form-check-label">
                                  <input
                                    type="radio"
                                    name="user_status"
                                    className="form-check-input"
                                    value="1"
                                    checked={userData.user_status == "1"}
                                    onChange={handleStatusChange}
                                  />
                                  Active
                                </label>
                              </div>
                              <div className="form-check-inline">
                                <label className="form-check-label">
                                  <input
                                    type="radio"
                                    name="user_status"
                                    className="form-check-input"
                                    value="0"
                                    checked={userData.user_status == "0"}
                                    onChange={handleStatusChange}
                                  />
                                  Inactive
                                </label>
                              </div>
                            </div>
                          </div>

                          {/* Submit */}
                          <div className="col-12">
                            <div className="text-end">
                              <button
                                type="submit"
                                className="btn btn-primary me-2"
                                disabled={loading}
                              >
                                {loading ? "Updating..." : "Submit"}
                              </button>
                              <Link
                                to="/list-users"
                                className="btn btn-secondary"
                              >
                                Cancel
                              </Link>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default EditUser;
